import { React, useRef, useState, useEffect, Typography, Grid2, Tabs, Tab, Outlet, TopBar, AppLayout, Box, CustomButton, IconButton, ProposalSummary, ProposalWIP, ProposalAttachments, ProposalNotes } from "../../utils/globalImports";
import { useNavigate, useLocation, Location, useParams, ArrowBackIosIcon, Dayjs, dayjs, useApi, Toaster, validationMessages, ProposalGeneral, GeneralRef, ProposalFeeStage, ProposalInstructionLog, ProposalStage, CustomConfirmDialog } from '../../utils/globalImports';
// import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { majorRoutes } from "../../constants/majorRoutes";
import { useFormUnsavedChanges } from "../../hooks/useFormUnsavedChanges";
import { headingStyles } from "../../assets/styles/CommonStyles";



const tabStyles = {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "16px",
    color: "#1e66ab",
    borderBottom: "2px solid #d3d3d3",
    flexGrow: 1,          // ensures even space
    justifyContent: "center", // centers text
    "&.Mui-selected": {
        backgroundColor: "#1e66ab",
        color: "white",
        borderRadius: "8px 8px 0 0",
    },
};

const tabs = [
    { value: 1, label: "General", alwaysEnabled: true },
    { value: 2, label: "Summary" },
    { value: 4, label: "AFP/WIP/Pre billing" },
    { value: 5, label: "Notes / comments" },
    { value: 6, label: "Attachments" },
    { value: 7, label: "Feestage" },
    // { value: 8, label: "Third party cost" },
    //{ value: 9, label: "Forecast" },
    //{ value: 10, label: "Invoiced" },
    { value: 11, label: "Instructions" }
];

interface Proposal {
    projectNumber?: string;
    projectName?: string;
    projectId: number;
    proposalNumber: string;
    proposalName: string;
    proposalType: string;
    clientId?: number;
    proposalTags: ProposalTags[];
    proposalDueDate: Dayjs | string | null;
    startDate: Dayjs | string | null;
    endDate: Dayjs | string | null;
    proposalStageId: number;
    description: string;
    frameworks?: ProposalFramework[];
    proposalValue?: number;
    confidenceInterval?: number;
    confidenceWeightedValue?: number;
    proposalInstructedValue?: number;
    proposalUninstructedValue?: number;
    status?: number;
    createdId?: number;
    projectStartDate: Dayjs | string | null;
    projectEndDate: Dayjs | string | null;
}

interface ProposalTags {
    proposalId?: number;
    tagId: number;
}

interface ProposalFramework {
    proposalId?: number;
    projectId: number;
    clientId: number;
    frameworkId: number;
    frameworkName: string;
    isDefault: number;
}

interface LocationState {
    fromTab?: number;
}

const AddProposal: React.FC = () => {

    const { execute } = useApi();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = React.useState(1);
    const [pendingTab, setPendingTab] = useState<number | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const proposalGeneralRef = useRef<GeneralRef>(null);
    const [proposal, setProposal] = useState<Proposal>();

    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL
    const { proposalId } = useParams<{ proposalId?: string }>(); // Get proposalId from URL
    const { projectName } = useParams<{ projectName?: string }>(); // Get proposalId from URL

    const [isProposalLoaded, setIsProposalLoaded] = useState(false);
    const role = localStorage.getItem("role");

    // // useUnsavedChanges(proposalGeneralRef.current?.isDirty!, "You have unsaved changes. Do you really want to discard them?")
    // const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    //     proposalGeneralRef.current?.isDirty!,
    //     "You have unsaved changes. Are you sure you want to leave?"
    // );

    const { ConfirmLeaveDialog } = useFormUnsavedChanges({
        isDirty,
        routesToBlock: majorRoutes
    });


    useEffect(() => {
        if (proposalId) {
            fetchProposal();
        }
    }, [proposalId]);


    const location = useLocation() as Location & { state: LocationState };
    const fromPage = location.state?.from;

    useEffect(() => {
        if (location.state?.fromTab) {
            setActiveTab(location.state.fromTab);
        }
    }, []);

    // useEffect(() => {
    //     if (prevTab === 7 && activeTab !== 7) {
    //         fetchProposal();
    //     }
    // }, [activeTab]);


    // fetch proposal
    const fetchProposal = async () => {
        const { data } = await execute<Proposal>('get', `/Proposal/proposals/${proposalId}/details`);
        if (data) {
            setProposal(data);
            setIsProposalLoaded(true); // ✅ Mark as loaded
        }
        else {
            navigate('/404');  // Redirect to a Not Found page
        }
    };



    const handleNavigate = async (shouldDelay = false) => {
        if (fromPage) {
            navigate(fromPage);
        }
        else{
            navigate(`/proposal/proposals/${projectId}/${projectName}`)
        }

    };

    const handleTabChange = (newTab: number) => {
        if (activeTab === 1 && isDirty) {
            setPendingTab(newTab);
            setShowConfirmDialog(true);
            return;
        }
        setActiveTab(newTab);
    };

    // handle add or edit proposal
    const handleProposalSubmit = async () => {
        proposalGeneralRef.current?.submitForm();
        setIsDirty(false); //  Reset BEFORE any navigation
    }

    const handleApproveProposal = async () => {
        proposalGeneralRef.current?.approveProposal();
        setIsDirty(false); //  Reset BEFORE any navigation
    }

    return (
        <AppLayout>
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%",   }}>
                <Grid2 container spacing={1} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid2 size={{ xs: 12, md: 6, lg: 8 }} sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton sx={{ width: '40px', height: '40px', backgroundColor: 'transparent' }} onClick={() => handleNavigate()} >
                            <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px' }} />
                        </IconButton>

                        <Typography variant="h6" noWrap sx={headingStyles}>
                            {proposalId ? 'Edit Proposal' : 'Add Proposal'}
                        </Typography>
                    </Grid2>

                    {proposal?.proposalStageId ===
                        ProposalStage.ReadyToReview
                        && role?.toLowerCase() == 'admin'
                        &&
                        (
                            <Grid2 size={{ xs: 12, md: 3, lg: 2 }}>
                                <CustomButton
                                    capsule
                                    type="button" // ✅ Add this line
                                    sx={{
                                        mt: 0,
                                        mb: 2,
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                    }}
                                    backgroundColor="#1e66ab"
                                    hoverColor="rgba(150, 193, 33, 0.75)"
                                    onClick={handleApproveProposal}
                                >
                                    Approve Proposal
                                </CustomButton>
                            </Grid2>
                        )}

                    <Grid2 size={{ xs: 12, md: 3, lg: 2 }}>
                        <CustomButton
                            capsule
                            type="button" // ✅ Add this line
                            sx={{
                                mt: 0,
                                mb: 2,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                            }}
                            onClick={handleProposalSubmit}
                        >
                            {proposalId ? 'Update Proposal' : 'Save Proposal'}
                        </CustomButton>
                    </Grid2>
                </Grid2>

                <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flexGrow: 1 }}>
                    <Tabs style={{ width: "100%", fontSize: "16px", position: "relative", left: "0%"}}
                        value={activeTab}
                        onChange={(_, newTab) => handleTabChange(newTab)}
                        aria-label="secondary tabs example"
                    >

                        {tabs.map((tab) => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                label={tab.label}
                                sx={tabStyles}
                                disabled={!tab.alwaysEnabled && !proposalId}
                            />
                        ))}

                    </Tabs>

                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingRight: 2,
                        paddingTop: 2
                    }}>
                        {
                            (() => {
                                switch (activeTab) {
                                    case 1:
                                        return <ProposalGeneral
                                            ref={proposalGeneralRef}
                                            proposalData={proposal}
                                            isProposalLoaded={isProposalLoaded}
                                            onDirtyChange={setIsDirty}
                                        />;
                                    case 2:
                                        return <ProposalSummary />;
                                    case 4:
                                        return <ProposalWIP proposalId={Number(proposalId)} />;
                                    case 5:
                                        return <ProposalNotes />;
                                    case 6:
                                        return <ProposalAttachments />;
                                    case 7:
                                        return <ProposalFeeStage
                                            proposalFramework={proposal?.frameworks}
                                            disabled={
                                                proposal?.proposalStageId!
                                                    >= ProposalStage.ReadyToReview
                                                    ? true : false
                                            } />;
                                    case 11:
                                        return <ProposalInstructionLog />;
                                    default:
                                        return null;
                                }
                            })()
                        }
                    </Box>

                </Box>
            </Box>

            <CustomConfirmDialog
                open={showConfirmDialog}
                title="Discard Changes"
                message="You have unsaved changes. Do you really want to discard them?"
                onConfirm={() => {
                    if (pendingTab !== null) {
                        setActiveTab(pendingTab);
                        setPendingTab(null);
                    }
                    setShowConfirmDialog(false);

                }}
                onCancel={() => {
                    setShowConfirmDialog(false);
                    setPendingTab(null);
                }}
                onClose={() => {
                    setShowConfirmDialog(false);
                    setPendingTab(null);
                }}
            />

            {/* <CustomConfirmDialog
                open={showDialog}
                title="Discard Changes"
                message={"hello"}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
                onClose={cancelNavigation}
            /> */}

            <ConfirmLeaveDialog />
        </AppLayout>
    );
};

export default AddProposal;